import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	Alert,
	Box,
	Chip,
	Paper,
	Divider,
	Grid,
	CircularProgress,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Title } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { MCPFeedback, MCPSessionDetail, getMCPSession } from '../../Services/quantiAI';

export const AIFeedbackDetail = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const feedback = location.state?.feedback as MCPFeedback | undefined;

	const [session, setSession] = useState<MCPSessionDetail | null>(null);
	const [sessionLoading, setSessionLoading] = useState(false);
	const [sessionError, setSessionError] = useState<string | null>(null);

	useEffect(() => {
		const loadSession = async () => {
			if (!feedback?.session_id) return;

			setSessionLoading(true);
			setSessionError(null);
			try {
				const data = await getMCPSession(feedback.session_id);
				setSession(data);
			} catch (err: any) {
				setSessionError(err.message || 'Impossible de charger la session');
			} finally {
				setSessionLoading(false);
			}
		};

		loadSession();
	}, [feedback?.session_id]);

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		return date.toLocaleString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	const formatDuration = (ms: number | undefined) => {
		if (!ms) return '-';
		if (ms < 1000) return `${ms}ms`;
		if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
		return `${(ms / 60000).toFixed(1)}min`;
	};

	const getRatingIcon = (rating: string) => {
		switch (rating) {
			case 'positive':
				return <ThumbUpIcon color="success" sx={{ fontSize: 40 }} />;
			case 'negative':
				return <ThumbDownIcon color="error" sx={{ fontSize: 40 }} />;
			default:
				return <RemoveIcon color="disabled" sx={{ fontSize: 40 }} />;
		}
	};

	const getRatingColor = (rating: string): 'success' | 'error' | 'default' => {
		switch (rating) {
			case 'positive':
				return 'success';
			case 'negative':
				return 'error';
			default:
				return 'default';
		}
	};

	const getRatingLabel = (rating: string): string => {
		switch (rating) {
			case 'positive':
				return 'Positif';
			case 'negative':
				return 'Négatif';
			case 'neutral':
				return 'Neutre';
			default:
				return rating;
		}
	};

	const getCategoryLabel = (category: string): string => {
		switch (category) {
			case 'accuracy':
				return 'Précision';
			case 'relevance':
				return 'Pertinence';
			case 'completeness':
				return 'Complétude';
			case 'speed':
				return 'Rapidité';
			case 'other':
				return 'Autre';
			default:
				return category;
		}
	};

	if (!feedback) {
		return (
			<Card>
				<CardContent>
					<Alert severity="warning">
						Feedback non trouvé. Retournez à la liste pour sélectionner un feedback.
					</Alert>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-feedback')}
						sx={{ mt: 2 }}
					>
						Retour à la liste
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<Title title="Détail du feedback" />
			<CardContent>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-feedback')}
					>
						Retour à la liste
					</Button>
				</Box>

				{/* Rating principal */}
				<Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
						{getRatingIcon(feedback.rating)}
						<Chip
							label={getRatingLabel(feedback.rating)}
							color={getRatingColor(feedback.rating)}
							sx={{ fontSize: '1.1rem', py: 2.5, px: 1 }}
						/>
					</Box>
					{feedback.category && (
						<Chip
							label={getCategoryLabel(feedback.category)}
							variant="outlined"
							sx={{ mt: 1 }}
						/>
					)}
				</Paper>

				{/* Informations détaillées */}
				<Paper sx={{ p: 2, mb: 3 }}>
					<Typography variant="h6" gutterBottom>
						Informations
					</Typography>
					<Divider sx={{ mb: 2 }} />
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6} md={4}>
							<Typography variant="body2" color="text.secondary">
								Utilisateur
							</Typography>
							<Typography variant="body1">
								{feedback.user_email || `User #${feedback.user_id}`}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<Typography variant="body2" color="text.secondary">
								Projet
							</Typography>
							<Typography variant="body1">
								{feedback.project_folder_id ? (
									<Chip label={feedback.project_folder_id} size="small" />
								) : (
									'-'
								)}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<Typography variant="body2" color="text.secondary">
								Date
							</Typography>
							<Typography variant="body1">
								{formatDate(feedback.created_at)}
							</Typography>
						</Grid>
						{feedback.tool_name && (
							<Grid item xs={12} sm={6} md={4}>
								<Typography variant="body2" color="text.secondary">
									Tool utilisé
								</Typography>
								<Chip label={feedback.tool_name} size="small" color="info" />
							</Grid>
						)}
					</Grid>
				</Paper>

				{/* Question originale */}
				{feedback.original_question && (
					<Paper sx={{ p: 2, mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							Question originale
						</Typography>
						<Divider sx={{ mb: 2 }} />
						<Paper
							sx={{
								p: 2,
								bgcolor: 'grey.50',
								borderLeft: 4,
								borderColor: 'primary.main',
							}}
						>
							<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
								{feedback.original_question}
							</Typography>
						</Paper>
					</Paper>
				)}

				{/* Commentaire */}
				{feedback.comment && (
					<Paper sx={{ p: 2, mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							Commentaire de l'utilisateur
						</Typography>
						<Divider sx={{ mb: 2 }} />
						<Paper
							sx={{
								p: 2,
								bgcolor: feedback.rating === 'positive' ? 'success.50' : feedback.rating === 'negative' ? 'error.50' : 'grey.50',
								borderLeft: 4,
								borderColor: feedback.rating === 'positive' ? 'success.main' : feedback.rating === 'negative' ? 'error.main' : 'grey.400',
							}}
						>
							<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
								{feedback.comment}
							</Typography>
						</Paper>
					</Paper>
				)}

				{/* Session associée */}
				{feedback.session_id && (
					<Paper sx={{ p: 2, mb: 3 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<Typography variant="h6">
								Session associée
							</Typography>
							<Button
								size="small"
								endIcon={<OpenInNewIcon />}
								onClick={() => navigate(`/ai-conversations/${feedback.session_id}`)}
							>
								Voir la session complète
							</Button>
						</Box>
						<Divider sx={{ mb: 2 }} />

						{sessionLoading && (
							<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
								<CircularProgress size={30} />
							</Box>
						)}

						{sessionError && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{sessionError}
							</Alert>
						)}

						{session && (
							<>
								{/* Info session */}
								<Grid container spacing={2} sx={{ mb: 2 }}>
									<Grid item xs={6} sm={3}>
										<Typography variant="body2" color="text.secondary">
											Messages
										</Typography>
										<Typography variant="body1">{session.message_count}</Typography>
									</Grid>
									<Grid item xs={6} sm={3}>
										<Typography variant="body2" color="text.secondary">
											Tool calls
										</Typography>
										<Typography variant="body1">{session.tool_call_count}</Typography>
									</Grid>
									<Grid item xs={6} sm={3}>
										<Typography variant="body2" color="text.secondary">
											Durée totale
										</Typography>
										<Typography variant="body1">
											{formatDuration(session.total_duration_ms)}
										</Typography>
									</Grid>
									<Grid item xs={6} sm={3}>
										<Typography variant="body2" color="text.secondary">
											Client
										</Typography>
										<Typography variant="body1">{session.client_type || '-'}</Typography>
									</Grid>
								</Grid>

								{/* Messages de la session */}
								{session.messages && session.messages.length > 0 && (
									<>
										<Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
											Messages ({session.messages.length})
										</Typography>
										{session.messages.slice(0, 10).map((msg, index) => (
											<Accordion key={msg.id} defaultExpanded={index < 3}>
												<AccordionSummary expandIcon={<ExpandMoreIcon />}>
													<Box
														sx={{
															display: 'flex',
															alignItems: 'center',
															gap: 1,
															width: '100%',
															pr: 2,
														}}
													>
														{msg.success ? (
															<CheckCircleIcon color="success" fontSize="small" />
														) : (
															<ErrorIcon color="error" fontSize="small" />
														)}
														<Chip
															label={msg.tool_name || msg.method}
															size="small"
															color={msg.tool_name ? 'primary' : 'default'}
														/>
														{msg.duration_ms && (
															<Typography variant="body2" color="text.secondary">
																{formatDuration(msg.duration_ms)}
															</Typography>
														)}
														<Typography
															variant="body2"
															color="text.secondary"
															sx={{ ml: 'auto' }}
														>
															{formatDate(msg.created_at)}
														</Typography>
													</Box>
												</AccordionSummary>
												<AccordionDetails>
													{msg.tool_arguments && (
														<Box sx={{ mb: 2 }}>
															<Typography variant="subtitle2" gutterBottom>
																Arguments
															</Typography>
															<Paper
																sx={{
																	p: 1,
																	bgcolor: 'grey.100',
																	fontFamily: 'monospace',
																	fontSize: '0.75rem',
																	overflow: 'auto',
																	maxHeight: 150,
																}}
															>
																<pre style={{ margin: 0 }}>
																	{JSON.stringify(msg.tool_arguments, null, 2)}
																</pre>
															</Paper>
														</Box>
													)}
													{msg.result_text && (
														<Box sx={{ mb: 2 }}>
															<Typography variant="subtitle2" gutterBottom>
																Résultat{' '}
																{msg.result_size && (
																	<Typography
																		component="span"
																		variant="body2"
																		color="text.secondary"
																	>
																		({msg.result_size} bytes)
																	</Typography>
																)}
															</Typography>
															<Paper
																sx={{
																	p: 1,
																	bgcolor: 'grey.100',
																	fontFamily: 'monospace',
																	fontSize: '0.75rem',
																	overflow: 'auto',
																	maxHeight: 200,
																	whiteSpace: 'pre-wrap',
																}}
															>
																{msg.result_text}
															</Paper>
														</Box>
													)}
													{msg.error_message && (
														<Alert severity="error">{msg.error_message}</Alert>
													)}
												</AccordionDetails>
											</Accordion>
										))}
										{session.messages.length > 10 && (
											<Alert severity="info" sx={{ mt: 1 }}>
												{session.messages.length - 10} messages supplémentaires.{' '}
												<Typography
													component="span"
													sx={{
														color: 'primary.main',
														cursor: 'pointer',
														textDecoration: 'underline',
													}}
													onClick={() => navigate(`/ai-conversations/${feedback.session_id}`)}
												>
													Voir la session complète
												</Typography>
											</Alert>
										)}
									</>
								)}
							</>
						)}
					</Paper>
				)}

				{/* ID technique */}
				<Paper sx={{ p: 2 }}>
					<Typography variant="body2" color="text.secondary">
						ID du feedback
					</Typography>
					<Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
						{feedback.id}
					</Typography>
				</Paper>
			</CardContent>
		</Card>
	);
};
